import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import "./Search.css";
import { useTranslation } from "react-i18next";
import { tField } from "../../../utils/helpers";
import { IoSearchOutline, IoMapOutline } from "react-icons/io5";
import i18n from "i18next";

function HomeSearchForm({ categories, states }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({
    property_for: 'rent',
    category_id: '',
    city_ids: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('search_form_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormValues({
          property_for: parsed.property_for || 'rent',
          category_id:  parsed.category_id  || '',
          city_ids:     parsed.city_ids      || [],
        });
      }
    } catch (_) {}
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      try { localStorage.setItem('search_form_data', JSON.stringify(formValues)); }
      catch (_) {}
    }
  }, [formValues, isLoading]);

  const selectStyles = {
    control: (p, s) => ({
      ...p,
      border: s.isFocused ? '1.5px solid #3e9a73' : '1.5px solid #e8eee8',
      borderRadius: '12px',
      boxShadow: s.isFocused ? '0 0 0 3px rgba(62,154,115,0.12)' : 'none',
      minHeight: '46px',
      background: '#fff',
      transition: 'all 0.2s',
      '&:hover': { borderColor: '#3e9a73' },
      flexWrap: 'nowrap',
    }),
    singleValue: (p) => ({ ...p, color: '#1a2332', fontWeight: '500', fontSize: 14 }),
    placeholder: (p) => ({ ...p, color: '#aab4aa', fontSize: 14, whiteSpace: 'nowrap' }),
    multiValue: (p) => ({ ...p, backgroundColor: '#edf7f2', borderRadius: '6px' }),
    multiValueLabel: (p) => ({ ...p, color: '#3e9a73', fontWeight: '600', fontSize: 12 }),
    multiValueRemove: (p) => ({ ...p, color: '#3e9a73', ':hover': { backgroundColor: '#3e9a73', color: 'white' } }),
    option: (p, s) => ({
      ...p,
      backgroundColor: s.isSelected ? '#3e9a73' : s.isFocused ? '#f0f9f4' : '#fff',
      color: s.isSelected ? '#fff' : '#1a2332',
      fontWeight: s.isSelected ? '600' : '400',
      fontSize: 14, cursor: 'pointer'
    }),
    menu: (p) => ({
      ...p,
      borderRadius: '14px', border: '1.5px solid #e8eee8',
      boxShadow: '0 8px 28px rgba(0,0,0,0.10)', overflow: 'hidden', zIndex: 999
    }),
    valueContainer: (p) => ({ ...p, flexWrap: 'nowrap', overflow: 'hidden' }),
    indicatorsContainer: (p) => ({ ...p, flexShrink: 0 }),
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.append('property_for', formValues.property_for);
    if (formValues.category_id) params.append('category_id', formValues.category_id);
    formValues.city_ids.forEach(id => params.append('city_ids[]', id));
    navigate(`/search?${params.toString()}`);
  };

  const getSelectedCityOptions = () => {
    const out = [];
    states.forEach(s => s.cities.forEach(c => {
      if (formValues.city_ids.includes(c.id))
        out.push({ value: c.id, label: tField(c, 'name') });
    }));
    return out;
  };

  const categoryOptions = [
    { value: '', label: t('home.search.fields.category.all') },
    ...categories.map(cat => ({ value: cat.id, label: tField(cat, 'name') }))
  ];

  const selectedCategory = formValues.category_id
    ? { value: formValues.category_id, label: categories.find(c => c.id === formValues.category_id)?.['name_' + i18n.language] }
    : categoryOptions[0];

  const typeOptions = [
    { value: 'rent',     label: t('home.search.purpose.rent') },
    { value: 'sale',     label: t('home.search.purpose.sale') },
    { value: 'exchange', label: t('home.search.purpose.exchange') },
  ];

  return (
    <div className="search-form-wrapper">

      {/* ── Row 1: Purpose chips ── */}
      <div className="search-purpose-row">
        {typeOptions.map(opt => (
          <button
            key={opt.value}
            type="button"
            className={'search-chip search-chip--type' + (formValues.property_for === opt.value ? ' active' : '')}
            onClick={() => setFormValues({ ...formValues, property_for: opt.value })}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* ── Row 2: Category + City + Search ── */}
      <div className="search-selects-row">
        <div className="search-category-select">
          <Select
            placeholder={t('home.search.fields.category.all')}
            options={categoryOptions}
            value={selectedCategory}
            onChange={opt => setFormValues({ ...formValues, category_id: opt ? opt.value : '' })}
            styles={selectStyles}
            isSearchable={false}
          />
        </div>
        <div className="search-city-select">
          <Select
            placeholder={t('home.search.fields.city.placeholder')}
            options={states.map(s => ({
              label: tField(s, 'name'),
              options: s.cities.map(c => ({ value: c.id, label: tField(c, 'name') }))
            }))}
            value={getSelectedCityOptions()}
            onChange={opts => setFormValues({ ...formValues, city_ids: opts.map(o => o.value) })}
            styles={selectStyles}
            isMulti
            closeMenuOnSelect={false}
          />
        </div>
        <button type="button" className="search-submit-btn" onClick={handleSearch}>
          <IoSearchOutline size={20} />
          <span>{t('home.search.button')}</span>
        </button>
      </div>

      <button type="button" className="search-map-link" onClick={() => navigate('/map-search')}>
        <IoMapOutline size={16} />
        البحث على الخريطة
      </button>

    </div>
  );
}

export default HomeSearchForm;
